import type { HighlightMergeFieldKey } from "../types/templateMerge";

/**
 * Document placeholders: yellow-highlight text **or** plain `[LABEL]` in the body (ALL CAPS inside brackets).
 * Text is normalized (trim, spaces, strip `[]`, uppercase) for matching. Edit only this file to add labels.
 *
 * Form resolution uses `getFormValueForMergeField` in `mergeFieldValues.ts`
 * (e.g. clientAddress → `form.addressMerged`, phone → `form.phone`).
 */
export type HighlightLabelRule = {
  /** UPPERCASE labels as they appear after normalization (no brackets). */
  labels: string[];
  mergeField: HighlightMergeFieldKey;
};

export const HIGHLIGHT_LABEL_RULES: HighlightLabelRule[] = [
  { labels: ["EFFECTIVE DATE"], mergeField: "effectiveDate" },
  { labels: ["CLIENT NAME"], mergeField: "legalEntityName" },
  { labels: ["REPRESENTATIVE NAME"], mergeField: "representativeName" },
  { labels: ["REPRESENTATIVE TITLE"], mergeField: "representativeTitle" },
  {
    labels: ["REPRESENTATIVE COMPANY"],
    mergeField: "representativeCompanyName",
  },
  { labels: ["REPRESENTATIVE ADDRESS"], mergeField: "representativeAddress" },
  {
    labels: ["REPRESENTATIVE STREET ADDRESS"],
    mergeField: "representativeStreetAddress",
  },
  {
    labels: ["REPRESENTATIVE CITY, STATE, ZIP"],
    mergeField: "representativeCityStateZip",
  },
  { labels: ["REPRESENTATIVE  PHONE"], mergeField: "representativePhone" },
  {
    labels: ["REPRESENTATIVE EMAIL ADDRESS"],
    mergeField: "representativeEmail",
  },
  { labels: ["CLIENT ADDRESS"], mergeField: "clientAddress" },
  {
    labels: ["COMPANY STREET ADDRESS"],
    mergeField: "companyStreetAddress",
  },
  {
    labels: ["COMPANY CITY, STATE, ZIP"],
    mergeField: "companyCityStateZip",
  },
  { labels: ["PHONE"], mergeField: "phone" },
  { labels: ["EMAIL ADDRESS"], mergeField: "email" },
  { labels: ["MSA EFFECTIVE DATE"], mergeField: "effectiveDate" },
  { labels: ["PLAN OR GROUP NAME"], mergeField: "planGroupName" },
  { labels: ["CLIENT TITLE"], mergeField: "contactTitle" },
  { labels: ["COMPANY NAME"], mergeField: "legalEntityName" },
];

/**
 * Match highlight body text to a merge field (e.g. "[EFFECTIVE DATE]" → effectiveDate).
 */
export const normalizeHighlightLabelText = (raw: string): string => {
  let s = raw.trim().replace(/\s+/g, " ");
  /** Templates sometimes prefix a date before the bracket label, e.g. `2024-04-21[EFFECTIVE DATE]`. */
  s = s.replace(/^\d{4}-\d{2}-\d{2}\s*/i, "");
  if (s.startsWith("[") && s.endsWith("]")) {
    s = s.slice(1, -1).trim();
  } else {
    const inBrackets = /\[([^\]]+)\]/.exec(s);
    if (inBrackets) {
      s = inBrackets[1].trim();
    }
  }
  return s.toUpperCase();
};

export const mergeFieldForHighlightText = (
  highlightText: string,
): HighlightMergeFieldKey | null => {
  const key = normalizeHighlightLabelText(highlightText);
  for (const rule of HIGHLIGHT_LABEL_RULES) {
    if (rule.labels.includes(key)) {
      return rule.mergeField;
    }
  }
  return null;
};
